// src/pages/b2b/sections/ProductDetailB2BInfo.jsx
import React from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Collapse,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import PictureAsPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import InsertDriveFileRounded from "@mui/icons-material/InsertDriveFileRounded";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { getPrimaryColor } from "../../../../GlobalFunction/Theme";

/* ---------------- i18n ---------------- */

const I18N_INFO = {
  ZH: {
    sections: {
      description: "商品說明",
      feature: "特點",
      instruction: "使用方法",
      safety: "注意事項",
      faq: "產品問答",
      specs: "產品詳情",
      attachments: "產品型錄",
    },
    specsLabels: {
      productName: "產品名稱",
      brandName: "品牌名稱",
      modelSku: "型號／編號",
      category: "產品類型",
      ingredients: "主要成分",
      instruction: "使用方法",
      safety: "安全注意事項",
      productType: "產品類型",
      capacity: "容量",
      usageScope: "適用範圍",
      PHRange: "pH 值範圍",
      fragrance: "香味",
      packagingMaterial: "包裝材質",
      dimension: "產品尺寸",
      weight: "重量",
      origin: "原產地",
      suggestedRetailPrice: "建議售價",
      barcode: "條碼／EAN",
      shippingMethod: "配送方式",
    },
    attachments: {
      view: "檢視",
      viewerTitle: "附件預覽",
      close: "關閉",
      unknownType: "一般檔案",
      unknownSize: "未提供檔案大小",
    },
    pdf: {
      viewManual: "請參閱產品附帶的說明書（PDF：642KB）",
      loginHint: "註冊後可下載的 PDF 手冊。",
    },
  },

  EN: {
    sections: {
      description: "Product Description",
      feature: "Features",
      instruction: "How to Use",
      safety: "Precautions",
      faq: "FAQ",
      specs: "Product Details",
      attachments: "Product Catalog",
    },
    specsLabels: {
      productName: "Product Name",
      brandName: "Brand",
      modelSku: "Model / Item No.",
      category: "Category",
      ingredients: "Main Ingredients",
      instruction: "How to Use",
      safety: "Safety Precautions",
      productType: "Product Type",
      capacity: "Capacity",
      usageScope: "Usage Scope",
      PHRange: "pH Range",
      fragrance: "Fragrance",
      packagingMaterial: "Packaging Material",
      dimension: "Dimensions",
      weight: "Weight",
      origin: "Country of Origin",
      suggestedRetailPrice: "Suggested Retail Price",
      barcode: "Barcode / EAN",
      shippingMethod: "Shipping Method",
    },
    attachments: {
      view: "View",
      viewerTitle: "Attachment Preview",
      close: "Close",
      unknownType: "File",
      unknownSize: "File size unavailable",
    },
    pdf: {
      viewManual: "View the included product manual (PDF: 642KB)",
      loginHint: "Log in to download the PDF manual.",
    },
  },

  JP: {
    sections: {
      description: "商品説明",
      feature: "特長",
      instruction: "使用方法",
      safety: "注意事項",
      faq: "よくある質問",
      specs: "製品詳細",
      attachments: "製品カタログ",
    },
    specsLabels: {
      productName: "製品名",
      brandName: "ブランド名",
      modelSku: "型番／品番",
      category: "製品カテゴリ",
      ingredients: "主成分",
      instruction: "使用方法",
      safety: "安全上の注意",
      productType: "製品タイプ",
      capacity: "容量",
      usageScope: "適用範囲",
      PHRange: "pH範囲",
      fragrance: "香り",
      packagingMaterial: "包装材質",
      dimension: "製品サイズ",
      weight: "重量",
      origin: "原産地",
      suggestedRetailPrice: "希望小売価格",
      barcode: "バーコード／EAN",
      shippingMethod: "配送方法",
    },
    attachments: {
      view: "表示",
      viewerTitle: "添付ファイルのプレビュー",
      close: "閉じる",
      unknownType: "ファイル",
      unknownSize: "ファイルサイズ情報なし",
    },
    pdf: {
      viewManual: "同梱の取扱説明書（PDF：642KB）をご参照ください",
      loginHint: "登録後にPDFマニュアルをダウンロードできます。",
    },
  },
};

/* ---------------- UI helpers ---------------- */

function Row({ title, children, defaultOpen = false }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const toggle = () => setOpen((v) => !v);

  return (
    <Paper elevation={0} sx={{ borderRadius: 0, overflow: "hidden", mb: 4 }}>
      <Box
        onClick={toggle}
        sx={{
          bgcolor: getPrimaryColor("b2b"),
          color: "#fff",
          px: { xs: 2, md: 3 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            fontSize: { xs: 16, md: 18 },
          }}
        >
          {title}
        </Typography>

        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          sx={{
            width: 36,
            height: 36,
            color: "#fff",
            "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
          }}
        >
          {open ? <RemoveIcon /> : <AddIcon />}
        </IconButton>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ bgcolor: "#fff", p: { xs: 2, md: 3 } }}>{children}</Box>
      </Collapse>
    </Paper>
  );
}

// simple helper to reuse everywhere
const isEmpty = (v) => {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  return false;
};

/**
 * Make bullets start on a new line:
 * - ● / •
 * - 1) / 1.
 * - A) / A.
 */
function normalizeBulletNewlines(input) {
  if (input === null || input === undefined) return "";
  let out = String(input);

  // ----------------------------
  // 0) Treat rn as a line break
  // ----------------------------

  // A) escaped sequences stored as text: "\\r\\n", "\\n", "\\r"
  out = out
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n");

  // B) real CRLF/CR/LF
  out = out.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // C) literal "rn" that is meant to be a newline (common after bad escaping)
  //    - converts: " rn " / "rn●" / "rn※" / "rn1." etc into "\n"
  out = out.replace(/[ \t]*rn[ \t]*/gi, "\n");

  // ----------------------------
  // 1) Force bullet-like tokens onto new lines
  // ----------------------------

  // bullet symbols: ● • ・ ○ ◯ ※
  out = out.replace(/(^|[ \t])([●•・○◯※])\s*/gm, "$1\n$2 ");

  // numbered bullets: 1. / 1) / (1)
  out = out.replace(/(^|[ \t])(\(?\d{1,3}\)?\s*[.)])\s*/gm, "$1\n$2 ");

  // letter bullets: A. / A) / (A)
  out = out.replace(/(^|[ \t])(\(?[A-Za-z]\)?\s*[.)])(?=\s)/gm, "$1\n$2");

  // dash/star list items when they look like list markers
  out = out.replace(/(^|[ \t])([-–—*])\s+/gm, "$1\n$2 ");

  // ----------------------------
  // 2) Cleanup
  // ----------------------------
  out = out
    .replace(/[ \t]+\n/g, "\n") // trailing spaces before newline
    .replace(/\n{3,}/g, "\n\n") // avoid huge gaps
    .replace(/\n[ \t]+/g, "\n") // trim indentation after newline
    .trim()
    .replace(/^\n+/, "");

  return out;
}

function TextBlock({ value, sx }) {
  const text = normalizeBulletNewlines(value);
  if (!text) return null;

  return (
    <Typography
      sx={{
        lineHeight: 1.9,
        fontWeight: 700,
        whiteSpace: "pre-line",
        wordBreak: "break-word",
        ...sx,
      }}
    >
      {text}
    </Typography>
  );
}

function SpecValue({ value }) {
  const text = normalizeBulletNewlines(value);
  if (!text) return null;

  return (
    <Typography
      sx={{
        lineHeight: 1.9,
        whiteSpace: "pre-line",
        wordBreak: "normal",
        overflowWrap: "break-word",
        fontSize: 14,
      }}
    >
      {text}
    </Typography>
  );
}

/* ---------------- WP field helpers ---------------- */

const pick = (obj, keys = []) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v === null || v === undefined) continue;
    const s = typeof v === "string" ? v.trim() : String(v).trim();
    if (s && s !== "null" && s !== "undefined") return s;
  }
  return "";
};

/**
 * Get "main" object for the current language.
 * Your ProductInfo page shows WP response can contain:
 *   product.main (ZH)
 *   product.mainEN / product.mainJP
 *   product.mainInfoEN / product.mainInfoJP
 * We choose based on lang, fallback to product.main.
 */
function getMainByLang(product, lang = "ZH") {
  const L = String(lang || "ZH").toUpperCase();

  if (L === "EN") {
    return (
      product?.mainEN ||
      product?.mainInfoEN ||
      product?.mainInfoTranslationEN ||
      product?.main ||
      {}
    );
  }

  if (L === "JP") {
    return (
      product?.mainJP ||
      product?.mainInfoJP ||
      product?.mainInfoTranslationJP ||
      product?.main ||
      {}
    );
  }

  return (
    product?.main ||
    product?.mainZH ||
    product?.mainInfo ||
    product?.mainInfoTranslationZH ||
    {}
  );
}

/** Brand name now often lives in main["品牌名稱"] (WP export), fallback to old shape */
function getBrandName(product, main) {
  return (
    pick(main, ["品牌名稱", "brandName", "品牌", "BrandName"]) ||
    pick(product?.Brand, ["brandName", "name"]) ||
    pick(product, ["brandName", "brand", "Brand"]) ||
    ""
  );
}

/** Product name now in main["產品名稱"] (WP export), fallback to legacy product.productName */
function getProductName(product, main) {
  return (
    pick(main, [
      "產品名稱",
      "商品名",
      "商品名稱",
      "商品系列名稱",
      "name",
      "productName",
    ]) ||
    pick(product, ["productName", "name", "ProductName", "Name"]) ||
    ""
  );
}

/** SKU / item no. now often in main["編號"] */
function getSku(product, main) {
  return (
    pick(product, ["productId", "productID", "id"]) ||
    pick(main, ["編號", "型號", "品番", "SKU", "sku"]) ||
    ""
  );
}

/** Read a detail field from main with WP keys + legacy keys */
function getDetail(main, kind) {
  if (!main) return "";

  // WP exports (commonly used in your DB sheets)
  if (kind === "description") {
    return pick(main, [
      "商品説明",
      "商品說明",
      "商品说明",
      "產品説明",
      "產品說明",
      "產品说明",
      "說明",
      "説明",
      "description",
      "Description",
      "productDescription",
      "ProductDescription",
    ]);
  }
  if (kind === "ingredients") {
    return pick(main, ["主要成分", "成分", "ingredients", "Ingredients"]);
  }
  if (kind === "instruction") {
    return pick(main, ["使用方法", "instruction", "Instruction"]);
  }
  if (kind === "safety") {
    return pick(main, [
      "注意事項",
      "安全注意事項",
      "safetyPrecaution",
      "SafetyPrecaution",
    ]);
  }
  if (kind === "faq") {
    return pick(main, ["產品問答", "問答", "faq", "FAQ"]);
  }

  // specs
  if (kind === "capacity") return pick(main, ["容量", "capacity"]);
  if (kind === "usageScope") return pick(main, ["適用範圍", "usageScope"]);
  if (kind === "PHRange")
    return pick(main, ["pH 值範圍", "pH值範圍", "PHRange", "pHRange"]);
  if (kind === "fragrance") return pick(main, ["香味", "fragrance"]);
  if (kind === "packagingMaterial")
    return pick(main, ["包裝材質", "packagingMaterial"]);
  if (kind === "dimension")
    return pick(main, ["產品尺寸", "尺寸", "dimension"]);
  if (kind === "weight") return pick(main, ["重量", "weight"]);
  if (kind === "origin") return pick(main, ["原產地", "origin"]);
  if (kind === "suggestedRetailPrice")
    return pick(main, [
      "建議售價",
      "建議零售價",
      "sugestedRetailPrice",
      "suggestedRetailPrice",
    ]);
  if (kind === "barcode") return pick(main, ["條碼／EAN", "條碼", "barcode"]);
  if (kind === "shippingMethod")
    return pick(main, ["配送方式", "shippingMethod"]);

  return "";
}

function formatAttachmentSize(value, lang = "ZH") {
  const bytes = Number(value);

  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "";
  }

  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );

  const formattedValue = bytes / 1024 ** unitIndex;
  const locale = lang === "EN" ? "en-US" : lang === "JP" ? "ja-JP" : "zh-TW";

  return `${formattedValue.toLocaleString(locale, {
    maximumFractionDigits: unitIndex === 0 ? 0 : 1,
  })} ${units[unitIndex]}`;
}

function getAttachmentType(attachment, fallback) {
  const mimeType = String(attachment?.mimeType || "").trim();

  if (mimeType) {
    return mimeType;
  }

  const fileName = String(attachment?.fileName || "").trim();

  const extension = fileName.includes(".")
    ? fileName.split(".").pop().toUpperCase()
    : "";

  return extension || fallback;
}

function getPdfViewerUrl(fileUrl) {
  const cleanUrl = String(fileUrl || "")
    .split("#")[0]
    .trim();

  if (!cleanUrl) {
    return "";
  }

  return `${cleanUrl}#toolbar=0&navpanes=0&scrollbar=0`;
}

function AttachmentFileIcon({ attachment }) {
  const mimeType = String(attachment?.mimeType || "").toLowerCase();

  const fileName = String(attachment?.fileName || "").toLowerCase();

  const isPdf = mimeType === "application/pdf" || fileName.endsWith(".pdf");

  if (isPdf) {
    return (
      <PictureAsPdfRounded
        sx={{
          color: "#d32f2f",
          fontSize: 42,
        }}
      />
    );
  }

  return (
    <InsertDriveFileRounded
      sx={{
        color: getPrimaryColor("b2b"),
        fontSize: 42,
      }}
    />
  );
}

/* ---------------- page ---------------- */

export default function ProductDetailB2BInfo({
  productId,
  product,
  isLoggedIn = false,
  pdfUrl = "/files/Test.pdf",
  lang = "ZH",
}) {
  const t = I18N_INFO[lang] || I18N_INFO.ZH;

  const [viewerAttachment, setViewerAttachment] = React.useState(null);

  const closeAttachmentViewer = () => {
    setViewerAttachment(null);
  };

  const attachments = Array.isArray(product?.attachments)
    ? product.attachments.filter(
        (attachment) => attachment?.url && attachment?.fileName,
      )
    : [];

  const main = getMainByLang(product, lang);

  const productName = getProductName(product, main);
  const brandName = getBrandName(product, main);
  const sku = getSku(product, main);

  // details (WP + legacy)
  const description = getDetail(main, "description");
  const ingredients = getDetail(main, "ingredients");
  const instruction = getDetail(main, "instruction");
  const safety = getDetail(main, "safety");
  const faq = getDetail(main, "faq");

  const capacity = getDetail(main, "capacity");
  const usageScope = getDetail(main, "usageScope");
  const PHRange = getDetail(main, "PHRange");
  const fragrance = getDetail(main, "fragrance");
  const packagingMaterial = getDetail(main, "packagingMaterial");
  const dimension = getDetail(main, "dimension");
  const weight = getDetail(main, "weight");
  const origin = getDetail(main, "origin");
  const suggestedRetailPrice = getDetail(main, "suggestedRetailPrice");
  const barcode = getDetail(main, "barcode");
  const shippingMethod = getDetail(main, "shippingMethod");

  // ---------- table specs ----------
  const rawSpecs = [
    { label: t.specsLabels.productName, value: productName },
    { label: t.specsLabels.brandName, value: brandName },
    { label: t.specsLabels.modelSku, value: sku },

    { label: t.specsLabels.ingredients, value: ingredients },
    { label: t.specsLabels.instruction, value: instruction },
    { label: t.specsLabels.safety, value: safety },

    { label: t.specsLabels.capacity, value: capacity },
    { label: t.specsLabels.usageScope, value: usageScope },
    { label: t.specsLabels.PHRange, value: PHRange },
    { label: t.specsLabels.fragrance, value: fragrance },
    { label: t.specsLabels.packagingMaterial, value: packagingMaterial },
    { label: t.specsLabels.dimension, value: dimension },
    { label: t.specsLabels.weight, value: weight },
    { label: t.specsLabels.origin, value: origin },
    { label: t.specsLabels.suggestedRetailPrice, value: suggestedRetailPrice },
    { label: t.specsLabels.barcode, value: barcode },
    { label: t.specsLabels.shippingMethod, value: shippingMethod },
  ];

  const specsToShow = rawSpecs.filter((r) => !isEmpty(r.value));

  // ---------- content checks for other sections ----------
  // (keep your original behavior: Features shows "usageScope")
  const hasDescription = !isEmpty(description);
  const hasFeature = !isEmpty(usageScope);
  const hasInstruction = !isEmpty(instruction);
  const hasSafety = !isEmpty(safety);
  const hasFaq = !isEmpty(faq);
  const hasSpecs = specsToShow.length > 0;
  const hasAttachments = attachments.length > 0;

  return (
    <Box>
      {/* Product Description */}
      {hasDescription && (
        <Row title={t.sections.description} defaultOpen>
          <TextBlock value={description} />
        </Row>
      )}

      {/* Features */}
      {hasFeature && (
        <Row title={t.sections.feature} defaultOpen>
          <TextBlock value={usageScope} />
        </Row>
      )}

      {/* How to use */}
      {hasInstruction && (
        <Row title={t.sections.instruction} defaultOpen>
          <TextBlock value={instruction} />
        </Row>
      )}

      {/* Precautions */}
      {hasSafety && (
        <Row title={t.sections.safety} defaultOpen>
          <TextBlock value={safety} />
        </Row>
      )}

      {/* FAQ */}
      {hasFaq && (
        <Row title={t.sections.faq} defaultOpen>
          <Typography
            sx={{
              lineHeight: 1.9,
              whiteSpace: "pre-line",
              wordBreak: "break-word",
            }}
          >
            {normalizeBulletNewlines(faq)}
          </Typography>
        </Row>
      )}

      {/* Specs */}
      {hasSpecs && (
        <Row title={t.sections.specs} defaultOpen>
          <Paper
            variant="outlined"
            sx={{ overflow: "hidden", borderRadius: 0, borderColor: "divider" }}
          >
            <Box sx={{ width: "100%" }}>
              <Table
                size="small"
                sx={{
                  width: "100%",
                  tableLayout: "fixed",
                  "& td, & th": {
                    borderColor: "divider",
                    px: { xs: 1, md: 2 },
                    py: { xs: 1, md: 1.25 },
                    verticalAlign: "top",
                  },
                  "& tr:nth-of-type(odd)": { bgcolor: "#fafafa" },
                }}
              >
                <TableBody>
                  {specsToShow.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell
                        sx={{
                          width: { xs: "35%", md: 220 },
                          fontWeight: 700,
                          bgcolor: "#f3f8fc",
                          borderRight: "1px solid #ddd",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {r.label}
                      </TableCell>

                      <TableCell
                        sx={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        <SpecValue value={r.value} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {/* PDF area (kept commented, translations already prepared above) */}
          {/* <Box
            sx={{
              mt: 3,
              py: 2,
              px: 2.5,
              borderTop: "1px solid #e0e0e0",
              bgcolor: "#fafafa",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <PictureAsPdfRounded sx={{ color: "#e53935" }} />
            {isLoggedIn ? (
              <Button
                href={pdfUrl}
                download
                sx={{
                  color: "#e53935",
                  fontWeight: 700,
                  fontSize: 15,
                  textTransform: "none",
                  p: 0,
                  "&:hover": {
                    textDecoration: "underline",
                    bgcolor: "transparent",
                  },
                }}
              >
                {t.pdf.viewManual}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t.pdf.loginHint}
              </Typography>
            )}
          </Box> */}
        </Row>
      )}

      {/* Product attachments */}
      {hasAttachments && (
        <Row title={t.sections.attachments} defaultOpen>
          <Box
            role="list"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {attachments.map((attachment, index) => {
              const formattedSize = formatAttachmentSize(
                attachment.fileSize,
                lang,
              );

              return (
                <Paper
                  key={attachment.id || `${attachment.url}-${index}`}
                  role="listitem"
                  variant="outlined"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    borderRadius: 1,
                    borderColor: "divider",
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      borderColor: getPrimaryColor("b2b"),
                      boxShadow: "0 3px 12px rgba(0, 0, 0, 0.08)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      minWidth: 0,
                    }}
                  >
                    <AttachmentFileIcon attachment={attachment} />

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: "#1d2327",
                          fontSize: 15,
                          fontWeight: 700,
                          lineHeight: 1.5,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {attachment.fileName}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<VisibilityRounded />}
                    onClick={() => setViewerAttachment(attachment)}
                    sx={{
                      bgcolor: getPrimaryColor("b2b"),
                      color: "#fff",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      textTransform: "none",
                      "&:hover": {
                        bgcolor: getPrimaryColor("b2b"),
                        filter: "brightness(0.9)",
                      },
                    }}
                  >
                    {t.attachments.view}
                  </Button>
                </Paper>
              );
            })}
          </Box>
        </Row>
      )}
      <Dialog
        open={Boolean(viewerAttachment)}
        onClose={closeAttachmentViewer}
        fullScreen
        aria-labelledby="product-attachment-viewer-title"
        PaperProps={{
          sx: {
            width: "100vw",
            maxWidth: "100vw",
            height: "100dvh",
            maxHeight: "100dvh",
            m: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle
          id="product-attachment-viewer-title"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            py: 1.5,
            pl: 2.5,
            pr: 1,
            bgcolor: getPrimaryColor("b2b"),
            color: "#fff",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="span"
              sx={{
                display: "block",
                fontSize: 18,
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              {t.attachments.viewerTitle}
            </Typography>

            {viewerAttachment?.fileName && (
              <Typography
                component="span"
                sx={{
                  display: "block",
                  mt: 0.25,
                  fontSize: 13,
                  lineHeight: 1.4,
                  opacity: 0.9,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {viewerAttachment.fileName}
              </Typography>
            )}
          </Box>

          <IconButton
            type="button"
            onClick={closeAttachmentViewer}
            aria-label={t.attachments.close}
            sx={{
              flexShrink: 0,
              color: "#fff",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.16)",
              },
            }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>

        <DialogContent
          onContextMenu={(event) => event.preventDefault()}
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            width: "100%",
            p: "0 !important",
            overflow: "hidden",
            bgcolor: "#525659",
          }}
        >
          {viewerAttachment?.url && (
            <Box
              component="iframe"
              title={viewerAttachment.fileName || t.attachments.viewerTitle}
              src={getPdfViewerUrl(viewerAttachment.url)}
              sx={{
                display: "block",
                width: "100%",
                height: "100%",
                border: 0,
                bgcolor: "#525659",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
